import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Send, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_venture-mentor-2/artifacts/52nzs5st_ChatGPT%20Image%20Jan%2022%2C%202026%2C%2002_22_56%20PM.png";

const ChatInterface = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [phase, setPhase] = useState("initial");
  const [sessionId, setSessionId] = useState(null);
  const [businessIdea, setBusinessIdea] = useState("");
  const [messages, setMessages] = useState([]);
  const [currentInput, setCurrentInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [questionsAsked, setQuestionsAsked] = useState(0);
  const messagesEndRef = useRef(null);

  // Handle resuming a saved session
  useEffect(() => {
    console.log("ChatInterface mounted, location.state:", location.state);
    if (location.state?.sessionId) {
      console.log("Resuming session:", location.state.sessionId);
      resumeSession(location.state.sessionId);
    }
  }, [location]);

  const resumeSession = async (sid) => {
    console.log("Starting resumeSession for:", sid);
    setIsLoading(true);
    try {
      const [sessionResponse, messagesResponse] = await Promise.all([
        axios.get(`${API}/sessions/${sid}`),
        axios.get(`${API}/messages/${sid}`)
      ]);
      
      const sessionData = sessionResponse.data;
      const sessionMessages = messagesResponse.data;
      
      console.log("Session data loaded:", sessionData);
      console.log("Messages loaded:", sessionMessages.length, "messages");
      
      if (sessionMessages.length > 0) {
        setSessionId(sid);
        setMessages(sessionMessages);
        setQuestionsAsked(sessionData.questions_asked);
        
        if (sessionData.phase === "ready_for_evaluation") {
          setPhase("ready_for_evaluation");
          console.log("Phase set to: ready_for_evaluation");
        } else if (sessionData.phase === "questioning") {
          setPhase("questioning");
          console.log("Phase set to: questioning");
        } else {
          setPhase("initial");
          console.log("Phase set to: initial");
        }
      }
    } catch (error) {
      console.error("Failed to resume session:", error);
      toast.error("Failed to load saved session");
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const wordCount = (text) => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const handleSubmitIdea = async () => {
    if (wordCount(businessIdea) < 100) {
      toast.error("Please provide at least 100 words describing your business idea.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/sessions`, {
        business_idea: businessIdea
      }, { withCredentials: true });

      setSessionId(response.data.session_id);
      setMessages([{
        role: "user",
        content: businessIdea,
        timestamp: new Date().toISOString()
      }]);
      setPhase("questioning");
      setBusinessIdea("");

      setTimeout(() => getNextQuestion(response.data.session_id), 500);
    } catch (error) {
      toast.error("Failed to start session. Please try again.");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getNextQuestion = async (sid) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sid || sessionId,
        content: "Please ask your first question."
      });

      setMessages(prev => [...prev, {
        role: "assistant",
        content: response.data.ai_response,
        timestamp: new Date().toISOString()
      }]);

      setQuestionsAsked(response.data.questions_asked);

      if (response.data.is_complete) {
        setPhase("ready_for_evaluation");
      }
    } catch (error) {
      toast.error("Failed to get question. Please try again.");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!currentInput.trim() || isLoading) return;

    const userMessage = currentInput;
    setCurrentInput("");
    setMessages(prev => [...prev, {
      role: "user",
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        content: userMessage
      });

      setMessages(prev => [...prev, {
        role: "assistant",
        content: response.data.ai_response,
        timestamp: new Date().toISOString()
      }]);

      setQuestionsAsked(response.data.questions_asked);

      if (response.data.is_complete || response.data.phase === "ready_for_evaluation") {
        setPhase("ready_for_evaluation");
      }
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEvaluate = async () => {
    setPhase("evaluating");
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/evaluate?session_id=${sessionId}`);
      toast.success("Evaluation complete!");
      navigate(`/results/${sessionId}`);
    } catch (error) {
      toast.error("Evaluation failed. Please try again.");
      console.error(error);
      setPhase("ready_for_evaluation");
    } finally {
      setIsLoading(false);
    }
  };

  const currentWordCount = wordCount(businessIdea);
  const isMinimumMet = currentWordCount >= 100;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="border-b border-[#E2E8F0] px-6 py-4">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate("/")}
            data-testid="header-title"
          >
            <img src={LOGO_URL} alt="9Mantra Logo" className="h-10 w-auto" />
            <h1 className="text-2xl font-normal text-[#1A1A1A]">9Mantra</h1>
          </div>
          {phase === "questioning" && (
            <div className="text-sm text-[#64748B]" data-testid="question-counter">
              Question {questionsAsked} of 10
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {phase === "initial" ? (
          <div className="flex-1 flex items-center justify-center px-6">
            <div className="max-w-2xl w-full">
              <h2 className="text-3xl md:text-4xl text-[#1A1A1A] mb-4 text-center" data-testid="initial-heading">
                Describe your business idea in detail
              </h2>
              
              {/* Enhanced Guidance Box */}
              <div className="bg-[#F8F9FA] border border-[#E2E8F0] p-6 mb-6 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-[#0047AB] mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-[#1A1A1A] font-medium mb-2">Minimum 100 words required</p>
                    <p className="text-sm text-[#64748B] mb-3">
                      The more detailed your description, the better our AI can evaluate your idea. Please include:
                    </p>
                    <ul className="text-sm text-[#64748B] space-y-1 ml-4">
                      <li>• What problem does your idea solve?</li>
                      <li>• Who are your target customers?</li>
                      <li>• What makes your solution unique?</li>
                      <li>• How will you generate revenue?</li>
                      <li>• What stage are you at currently?</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <Textarea
                value={businessIdea}
                onChange={(e) => setBusinessIdea(e.target.value)}
                placeholder="Example: My business idea is a subscription-based meal planning platform that uses AI to create personalized weekly meal plans based on dietary preferences, health goals, and budget constraints. The platform connects users to local grocery stores for seamless ingredient ordering and delivery. Our target market is busy professionals and health-conscious families aged 25-45 who struggle with meal planning. We differentiate through our AI personalization engine and partnerships with grocery chains. Revenue comes from monthly subscriptions ($9.99-$19.99) plus affiliate commissions. We currently have 500 beta users and strong early engagement metrics..."
                className="min-h-[320px] text-base p-4 mb-4 border-[#E2E8F0] focus:border-[#0047AB] focus:ring-[#0047AB]"
                data-testid="business-idea-input"
              />
              
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <span 
                    className={`text-sm font-medium ${
                      isMinimumMet ? 'text-[#10B981]' : 
                      currentWordCount > 50 ? 'text-[#F59E0B]' : 'text-[#EF4444]'
                    }`} 
                    data-testid="word-count"
                  >
                    {currentWordCount} / 100 words
                  </span>
                  {isMinimumMet && (
                    <CheckCircle className="h-4 w-4 text-[#10B981]" />
                  )}
                </div>
                <Button
                  onClick={handleSubmitIdea}
                  disabled={isLoading || !isMinimumMet}
                  className="bg-[#0047AB] hover:bg-[#003580] text-white px-6 py-3"
                  data-testid="submit-idea-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    <>Submit for AI Evaluation</>
                  )}
                </Button>
              </div>
              
              {!isMinimumMet && currentWordCount > 0 && (
                <p className="text-sm text-[#EF4444] mt-2 text-right">
                  Please add {100 - currentWordCount} more words for a comprehensive evaluation
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto px-6 py-8" data-testid="chat-messages">
              <div className="max-w-3xl mx-auto space-y-6">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    data-testid={`message-${idx}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-4 py-3 ${
                        msg.role === "user"
                          ? "bg-[#0047AB] text-white"
                          : "bg-[#F1F3F5] text-[#1A1A1A]"
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))}
                
                {isLoading && phase === "questioning" && (
                  <div className="flex justify-start" data-testid="loading-indicator">
                    <div className="bg-[#F1F3F5] text-[#1A1A1A] rounded-lg px-4 py-3">
                      <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Input Area */}
            {phase === "questioning" && (
              <div className="border-t border-[#E2E8F0] px-6 py-4 bg-white">
                <div className="max-w-3xl mx-auto flex gap-3">
                  <Textarea
                    value={currentInput}
                    onChange={(e) => setCurrentInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder="Type your answer..."
                    className="flex-1 min-h-[60px] resize-none border-[#E2E8F0] focus:border-[#0047AB] focus:ring-[#0047AB]"
                    data-testid="message-input"
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={isLoading || !currentInput.trim()}
                    className="bg-[#0047AB] hover:bg-[#003580] text-white px-6"
                    data-testid="send-message-btn"
                  >
                    <Send className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            )}

            {phase === "ready_for_evaluation" && (
              <div className="border-t border-[#E2E8F0] px-6 py-8 bg-[#F8F9FA]">
                <div className="max-w-3xl mx-auto text-center">
                  <CheckCircle className="h-12 w-12 text-[#10B981] mx-auto mb-4" />
                  <h3 className="text-xl text-[#1A1A1A] mb-2" data-testid="ready-heading">
                    Ready for Evaluation
                  </h3>
                  <p className="text-[#64748B] mb-6">
                    I have all the information needed. Let's evaluate your business idea against the 9 entrepreneurship mantras.
                  </p>
                  <Button
                    onClick={handleEvaluate}
                    disabled={isLoading}
                    className="bg-[#0047AB] hover:bg-[#003580] text-white px-8 py-3"
                    data-testid="evaluate-btn"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Evaluating...
                      </>
                    ) : (
                      <>Generate Evaluation</>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {phase === "evaluating" && (
              <div className="border-t border-[#E2E8F0] px-6 py-8 bg-[#F8F9FA]">
                <div className="max-w-3xl mx-auto text-center">
                  <Loader2 className="h-12 w-12 animate-spin text-[#0047AB] mx-auto mb-4" />
                  <h3 className="text-xl text-[#1A1A1A] mb-2">Evaluating Your Idea</h3>
                  <p className="text-[#64748B]">
                    Analyzing against 9 entrepreneurship frameworks, finding competitors, and sourcing research...
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ChatInterface;